import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getManageBnbBookings, getMyProperties } from '../../../api/services/propertyService';
import { filterPropertiesByScope } from '../../../utils/propertyScope';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './Payments.css';

const parseList = (res) => {
  const data = res?.data;
  return Array.isArray(data) ? data : [];
};

const formatMoneyKES = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 'KES 0';
  return `KES ${value.toLocaleString()}`;
};

const formatDate = (dateValue) => {
  if (!dateValue) return '—';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const Payments = () => {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingProperties(true);
      setError('');
      try {
        const res = await getMyProperties({
          page: 0,
          size: 100,
          search: '',
          dashboardScope: 'BNB',
        });
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        const scoped = filterPropertiesByScope(content, 'bnb', {
          includeUnscoped: true,
          inferFromUnits: true,
        });
        const list = (Array.isArray(scoped) ? scoped : [])
          .map((property) => ({
            id: property?.id,
            name: property?.name || 'Property',
          }))
          .filter((property) => property.id);

        if (!cancelled) {
          setProperties(list);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingProperties(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadPayments = useCallback(async () => {
    if (properties.length === 0) {
      setPayments([]);
      return;
    }

    setLoadingPayments(true);
    setError('');
    try {
      if (selectedPropertyId !== 'all') {
        const res = await getManageBnbBookings(selectedPropertyId, 'CONFIRMED');
        const rows = parseList(res).map((booking) => ({
          id: booking?.id,
          guestName:
            booking?.guestName ||
            [booking?.requesterFirstName, booking?.requesterLastName].filter(Boolean).join(' ') ||
            'Guest',
          amount: booking?.totalAmount || booking?.firstMonthPaidAmount || booking?.depositPaidAmount || 0,
          unitName: booking?.unitName || booking?.unitNumber || 'Unit',
          propertyName: booking?.propertyName || properties.find((p) => p.id === selectedPropertyId)?.name || 'Property',
          date: booking?.createdAt || booking?.confirmedAt || booking?.updatedAt || booking?.checkInDate || null,
        }));
        rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setPayments(rows);
        return;
      }

      const results = await Promise.all(
        properties.map((property) => getManageBnbBookings(property.id, 'CONFIRMED').then(parseList).catch(() => []))
      );
      const merged = results
        .flat()
        .map((booking) => ({
          id: booking?.id,
          guestName:
            booking?.guestName ||
            [booking?.requesterFirstName, booking?.requesterLastName].filter(Boolean).join(' ') ||
            'Guest',
          amount: booking?.totalAmount || booking?.firstMonthPaidAmount || booking?.depositPaidAmount || 0,
          unitName: booking?.unitName || booking?.unitNumber || 'Unit',
          propertyName:
            booking?.propertyName ||
            properties.find((property) => property.id === booking?.propertyId)?.name ||
            'Property',
          date: booking?.createdAt || booking?.confirmedAt || booking?.updatedAt || booking?.checkInDate || null,
        }));
      merged.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setPayments(merged);
    } catch (e) {
      setError(e?.message || 'Failed to load confirmed booking payments.');
      setPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  }, [properties, selectedPropertyId]);

  useEffect(() => {
    if (loadingProperties) return;
    loadPayments();
  }, [loadingProperties, loadPayments]);

  const filteredPayments = useMemo(() => {
    if (!search.trim()) return payments;
    const query = search.toLowerCase().trim();
    return payments.filter((payment) => {
      return (
        payment.guestName.toLowerCase().includes(query) ||
        payment.unitName.toLowerCase().includes(query) ||
        payment.propertyName.toLowerCase().includes(query)
      );
    });
  }, [payments, search]);

  return (
    <div className="payments-view">
      <CollapsibleTopPanel id="payments-filters" defaultOpenOnMobile={false}>
        <div className="payments-view__header">
          <h1 className="payments-view__title">Payments</h1>
          <p className="payments-view__subtitle">
            Confirmed booking payments showing guest name, amount, unit, and date.
          </p>
        </div>

        <div className="payments-view__toolbar">
          <div className="payments-view__field">
            <label htmlFor="payments-property">Property</label>
            <select
              id="payments-property"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              disabled={loadingProperties || loadingPayments}
            >
              <option value="all">All properties ({properties.length})</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
          </div>

          <div className="payments-view__field payments-view__field--search">
            <label htmlFor="payments-search">Search</label>
            <input
              id="payments-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user or unit..."
            />
          </div>
        </div>
      </CollapsibleTopPanel>

      {error && <div className="payments-view__alert payments-view__alert--error">{error}</div>}

      {loadingProperties || loadingPayments ? (
        <div className="payments-view__state">Loading payments...</div>
      ) : filteredPayments.length === 0 ? (
        <div className="payments-view__state payments-view__state--muted">
          No confirmed booking payments found.
        </div>
      ) : (
        <>
          <div className="payments-view__table-wrap">
            <table className="payments-view__table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Payment</th>
                  <th>Unit</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>
                      <div className="payments-view__user">{payment.guestName}</div>
                      <div className="payments-view__property">{payment.propertyName}</div>
                    </td>
                    <td>{formatMoneyKES(payment.amount)}</td>
                    <td>{payment.unitName}</td>
                    <td>{formatDate(payment.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="payments-view__cards">
            {filteredPayments.map((payment) => (
              <article key={payment.id} className="payments-view__card">
                <div className="payments-view__card-row">
                  <span className="payments-view__card-label">User</span>
                  <span className="payments-view__card-value">{payment.guestName}</span>
                </div>
                <div className="payments-view__card-row">
                  <span className="payments-view__card-label">Payment</span>
                  <span className="payments-view__card-value">{formatMoneyKES(payment.amount)}</span>
                </div>
                <div className="payments-view__card-row">
                  <span className="payments-view__card-label">Unit</span>
                  <span className="payments-view__card-value">{payment.unitName}</span>
                </div>
                <div className="payments-view__card-row">
                  <span className="payments-view__card-label">Date</span>
                  <span className="payments-view__card-value">{formatDate(payment.date)}</span>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Payments;
